/**
 * Last-known workspace accent, cached so a branded tenant does not flash
 * OmniMedia indigo on every reload.
 *
 * The flash is structural, not a race we can tune away: WorkspaceProvider
 * refuses to render children until GET /api/me resolves, and the accent
 * controller is one of those children — so nothing inside React can possibly
 * apply an accent before the network answers. The only fix is to remember the
 * last one and paint it at bundle entry.
 *
 * SECURITY: the record is keyed on the ACTIVE WORKSPACE ID and is dead the
 * moment that id is absent or different. That coupling is the whole design.
 * Four existing paths already clear omni-active-workspace-id — sign-out, the
 * Clerk user-switch invalidator in App.tsx, the stale-id sweep in
 * use-workspace.tsx, and Electron's localStorage wipe on sign-out (CB-148) —
 * so the persisted accent inherits all four and there is no fifth teardown
 * path for anyone to forget. A user who signs in as somebody else can never be
 * shown the previous tenant's colours.
 *
 * Logo and icon URLs are deliberately NOT persisted: they are presigned for an
 * hour, so a cached one would render as a broken image on the next cold load.
 * Text and colour only.
 */

import {
  ACTIVE_WORKSPACE_STORAGE_KEY,
  BRANDING_STORAGE_KEY,
} from "./storage-keys.ts";
import {
  applyWorkspaceAccent,
  clearWorkspaceAccent,
  resolveAccent,
} from "./workspace-branding.ts";

export { BRANDING_STORAGE_KEY };

export interface PersistedBranding {
  workspaceId: string;
  /** Canonical "H S% L%". */
  accent: string;
  /** Canonical "H S% L%". */
  foreground: string;
  /** Branded display name, for the pre-session loading screen. */
  name?: string;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * The security-relevant decision, extracted as a pure function so it is
 * testable without a DOM. Returns null whenever the record must not be trusted.
 */
export function resolvePersistedBranding(
  rawRecord: string | null,
  activeWorkspaceId: string | null,
): PersistedBranding | null {
  // No active workspace means signed out or freshly switched: the record is
  // orphaned regardless of what it says.
  if (!rawRecord || !activeWorkspaceId) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(rawRecord);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;

  const rec = parsed as Record<string, unknown>;
  if (typeof rec.workspaceId !== "string" || rec.workspaceId !== activeWorkspaceId) {
    return null;
  }
  if (typeof rec.accent !== "string") return null;

  // Re-validate on read: a value that cannot be rendered must not be applied,
  // and storage is not a trusted channel.
  const resolved = resolveAccent(rec.accent);
  if (!resolved) return null;

  return {
    workspaceId: rec.workspaceId,
    accent: resolved.accent,
    foreground:
      typeof rec.foreground === "string" && rec.foreground.trim()
        ? rec.foreground
        : resolved.foreground,
    name: typeof rec.name === "string" && rec.name.trim() ? rec.name : undefined,
  };
}

function defaultStorage(): StorageLike | null {
  try {
    // Access can throw outright in private mode / sandboxed iframes.
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

export function readPersistedBranding(
  storage: StorageLike | null = defaultStorage(),
): PersistedBranding | null {
  if (!storage) return null;
  try {
    return resolvePersistedBranding(
      storage.getItem(BRANDING_STORAGE_KEY),
      storage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY),
    );
  } catch {
    return null;
  }
}

export function writePersistedBranding(
  record: PersistedBranding,
  storage: StorageLike | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // A full or unavailable quota must never break sign-in.
  }
}

export function clearPersistedBranding(
  storage: StorageLike | null = defaultStorage(),
): void {
  if (!storage) return;
  try {
    storage.removeItem(BRANDING_STORAGE_KEY);
  } catch {
    // Ignore.
  }
}

/**
 * Paint the remembered accent before React's first commit, or self-clean if the
 * record is no longer trustworthy. Called once from main.tsx module scope.
 */
export function applyPersistedBrandingAtBoot(
  storage: StorageLike | null = defaultStorage(),
): PersistedBranding | null {
  const record = readPersistedBranding(storage);
  if (!record) {
    clearWorkspaceAccent();
    clearPersistedBranding(storage);
    return null;
  }
  applyWorkspaceAccent({ accent: record.accent, foreground: record.foreground });
  return record;
}
